import React from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { APP_BG } from '@/constant/theme/colors.constant';
import type { ColumnDef, SortState } from './types';
import { TABLE_TEST_IDS as TID } from '@/constant/testIds/tableHeader.testIds'; 
import { tid } from '@/utils/testId';

interface Props<T> {
  columns: ColumnDef<T>[];
  sortState?: SortState;
  onSort: (accessor: keyof T | string) => void;
  hasExpandableRows: boolean;
}

export function TableHeader<T>({
  columns,
  sortState,
  onSort,
  hasExpandableRows,
}: Props<T>) {
  return (
    <thead className="sticky top-0 z-20 shadow-sm" data-testid={tid(TID.HEADER)}>
      {/* Header row */}
      <tr>
        {columns.map((col, i) => {
          const isActive = sortState?.orderBy === col.accessor;
          const isAsc = isActive && sortState?.order === 'asc';
          const isDesc = isActive && sortState?.order === 'desc';
          const accessor = col.accessor as string;

          return (
            <th
              key={i}
              scope="col"
              data-testid={tid(TID.COLUMN_HEADER(accessor))}
              onClick={() => col.sorting && onSort(col.accessor)}
              className={[
                'px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest select-none',
                'transition-colors duration-150',
                'border-b border-slate-300/70 dark:border-sky-900/60',
                APP_BG.tableHeader,
                isAsc || isDesc
                  ? 'text-primary'
                  : 'text-slate-700 dark:text-sky-200/70 hover:text-slate-900 dark:hover:text-sky-100',
                col.sorting ? 'cursor-pointer' : 'cursor-default',
                col.width ?? '',
                col.headerClassName ?? '',
              ].join(' ')}
            >
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                {col.header}
                {col.sorting && (
                  <span 
                    className="flex-shrink-0 ml-auto"
                    data-testid={tid(TID.SORT_ICON(accessor, isActive ? sortState?.order : undefined))}
                  >
                    {isAsc ? (
                      <ChevronUpIcon
                        className="h-3 w-3 text-primary"
                        strokeWidth={3}
                      />
                    ) : isDesc ? (
                      <ChevronDownIcon
                        className="h-3 w-3 text-primary"
                        strokeWidth={3}
                      />
                    ) : (
                      <ChevronUpDownIcon
                        className="h-3 w-3 text-gray-400 dark:text-sky-900"
                        strokeWidth={2}
                      />
                    )}
                  </span>
                )}
              </div>
            </th>
          );
        })}
        {hasExpandableRows && (
          <th
            scope="col"
            data-testid={tid(TID.EXPAND_HEADER)}
            className={`w-10 px-3 py-3 border-b border-gray-200/60 dark:border-sky-900/60 ${APP_BG.tableHeader}`}
          />
        )}
      </tr>
    </thead>
  );
}
